import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaFilePdf, FaUpload, FaCheck } from 'react-icons/fa';
import { previewProgramImport, commitProgramImport } from '../../services/fitnessService';
import { trackUpgradePromptShown, trackUpgradePromptClick } from '../../utils/analytics';
import toast from 'react-hot-toast';
import './ProgramImportWizard.css';

const PREVIEW_PROGRESS_STEPS = [
  'Uploading PDF to server...',
  'Extracting text from document...',
  'Detecting layout format...',
  'Analyzing with AI model...',
  'Repairing and validating extracted structure...',
  'Finalizing preview...'
];

export default function ProgramImportWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [meta, setMeta] = useState(null);
  const [programName, setProgramName] = useState('');
  const [commitError, setCommitError] = useState(null);
  const [formatDetection, setFormatDetection] = useState(null);
  /** When true, import reuses catalog exercises whose names match (case-insensitive). */
  const [linkToExistingExercises, setLinkToExistingExercises] = useState(true);
  const [previewProgress, setPreviewProgress] = useState('');
  const [previewElapsedSec, setPreviewElapsedSec] = useState(0);
  const [previewError, setPreviewError] = useState(null);

  const handlePreview = async () => {
    if (!file) {
      toast.error('Choose a PDF file to import');
      return;
    }
    setLoading(true);
    setPreviewError(null);

    let progressStep = 0;
    const startedAt = Date.now();
    setPreviewProgress(PREVIEW_PROGRESS_STEPS[0]);
    setPreviewElapsedSec(0);

    const progressTimer = setInterval(() => {
      progressStep = Math.min(progressStep + 1, PREVIEW_PROGRESS_STEPS.length - 1);
      setPreviewProgress(PREVIEW_PROGRESS_STEPS[progressStep]);
      setPreviewElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 2500);

    try {
      const data = await previewProgramImport(file, { source: 'auto' });
      setBundle(data.bundle);
      setWarnings(data.warnings || []);
      setMeta(data.meta);
      setFormatDetection({
        detectedFormat: data.detectedFormat,
        detectionSignals: data.detectionSignals,
        detectionConfidence: data.detectionConfidence
      });
      setProgramName(data.bundle?.program?.name || '');
      setCommitError(null);
      setPreviewError(null);
      setStep(2);
      toast.success('Program extracted — review and import');
    } catch (e) {
      setPreviewError({
        message: e.message || 'Preview failed',
        context: e?.details?.parseError?.context || null,
        attempts: e?.details?.parseError?.attempts || null
      });
      toast.error(e.message || 'Preview failed');
    } finally {
      clearInterval(progressTimer);
      setPreviewProgress('');
      setPreviewElapsedSec(0);
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!bundle) return;
    const toCommit = {
      ...bundle,
      program: {
        ...bundle.program,
        name: programName.trim() || bundle.program.name
      }
    };
    setLoading(true);
    setCommitError(null);
    try {
      const res = await commitProgramImport(toCommit, { linkToExistingExercises });
      const linked = res.exercisesLinked ?? 0;
      const created = res.exercisesCreated ?? 0;
      const historySessionCount = res.historySessionCount ?? 0;
      if (linkToExistingExercises) {
        toast.success(
          `Program imported — linked ${linked} exercise${linked !== 1 ? 's' : ''} to the catalog, created ${created} new, imported ${historySessionCount} workout histor${historySessionCount === 1 ? 'y entry' : 'y entries'}.`
        );
      } else if (created > 0) {
        toast.success(
          `Program imported — ${created} new exercise${created !== 1 ? 's' : ''} created and ${historySessionCount} workout histor${historySessionCount === 1 ? 'y entry' : 'y entries'} imported.`
        );
      } else if (historySessionCount > 0) {
        toast.success(
          `Program imported — ${historySessionCount} workout histor${historySessionCount === 1 ? 'y entry' : 'y entries'} added.`
        );
      } else {
        toast.success('Program imported');
      }
      navigate(`/fitness/program/${res.programId}`);
    } catch (e) {
      const upgrade = e.upgrade === true;
      setCommitError({
        message: e.message || 'Import failed',
        upgrade,
        upgradeUrl: e.upgradeUrl || '/pricing#pricing-personal',
        upgradeHint: e.upgradeHint
      });
      if (upgrade) trackUpgradePromptShown('program_import');
      toast.error(e.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const sessionCount = bundle?.sessions?.length ?? 0;
  const historySessionCount = bundle?.historySessions?.length ?? 0;
  let exerciseLineCount = 0;
  if (bundle?.sessions) {
    for (const s of bundle.sessions) {
      for (const b of s.blocks || []) {
        exerciseLineCount += (b.exercises || []).length;
      }
    }
  }

  return (
    <div className="program-import-wizard">
      <div className="piw-header">
        <button
          type="button"
          className="piw-back"
          onClick={() => {
            if (step === 1) navigate(-1);
            else {
              setFormatDetection(null);
              setStep(1);
            }
          }}
        >
          <FaArrowLeft /> {step === 1 ? 'Back' : 'Upload another'}
        </button>
        <h1>Import program</h1>
        <p className="piw-sub">
          Bring in programs from common file formats—for example Trainerize PDF exports, with CSV and spreadsheets (Excel .xlsx / .xls) planned next. Text is extracted and interpreted with AI: Trainerize-style layouts are detected when possible, otherwise a generic parser runs. Review everything before saving.
        </p>
      </div>

      {step === 1 && (
        <div className="piw-card">
          <div className="piw-upload-zone">
            <FaFilePdf className="piw-pdf-icon" />
            <label className="piw-file-label">
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setFile(f || null);
                  setPreviewError(null);
                }}
              />
              <span>{file ? file.name : 'Choose file (PDF)'}</span>
            </label>
            <p className="piw-hint">
              Right now only PDF upload is enabled (max 25 MB). Uses your daily AI scan quota (same as photo exercise import). Additional formats will use this same flow when supported.
            </p>
          </div>
          <button type="button" className="piw-primary" onClick={handlePreview} disabled={loading || !file}>
            {loading ? 'Analyzing…' : (
              <>
                <FaUpload /> Extract &amp; preview
              </>
            )}
          </button>

          {loading && (
            <div className="piw-progress" role="status" aria-live="polite">
              <p className="piw-progress-msg">{previewProgress || 'Analyzing import...'}</p>
              <p className="piw-progress-time">Elapsed: {previewElapsedSec}s</p>
            </div>
          )}

          {previewError && (
            <div className="piw-preview-error" role="alert">
              <p className="piw-preview-error-title">Import preview failed</p>
              <p className="piw-preview-error-message">{previewError.message}</p>
              {previewError.attempts ? (
                <p className="piw-preview-error-attempts">Parser attempts: {previewError.attempts}</p>
              ) : null}
              {previewError.context ? (
                <pre className="piw-preview-error-context">{previewError.context}</pre>
              ) : null}
            </div>
          )}
        </div>
      )}

      {step === 2 && bundle && (
        <div className="piw-card piw-review">
          <h2>Review</h2>

          {commitError && (
            <div className="piw-limit-alert" role="alert">
              <p className="piw-limit-msg">{commitError.message}</p>
              {commitError.upgrade && (
                <div className="piw-limit-actions">
                  <Link
                    to={commitError.upgradeUrl}
                    className="piw-upgrade-link"
                    onClick={() => trackUpgradePromptClick('program_import')}
                  >
                    View plans with higher workout &amp; program limits
                  </Link>
                  {commitError.upgradeHint && (
                    <p className="piw-upgrade-hint">{commitError.upgradeHint}</p>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="piw-field">
            <label htmlFor="piw-name">Program name</label>
            <input
              id="piw-name"
              type="text"
              value={programName}
              onChange={(e) => setProgramName(e.target.value)}
            />
          </div>

          {meta && (
            <p className="piw-meta">
              PDF: {meta.pages} page(s), {meta.textLength?.toLocaleString?.() ?? meta.textLength} characters extracted
              {meta.truncated ? ' (truncated for AI — very long programs may be incomplete)' : ''}
            </p>
          )}

          {formatDetection?.detectedFormat && (
            <p className="piw-detected-format">
              Import path:{' '}
              <strong>{formatDetection.detectedFormat}</strong>
              {typeof formatDetection.detectionConfidence === 'number' && (
                <span className="piw-detected-confidence">
                  {' '}
                  ({Math.round(formatDetection.detectionConfidence * 100)}% detector confidence)
                </span>
              )}
              {formatDetection.detectionSignals?.length > 0 && (
                <span className="piw-detected-signals" title={formatDetection.detectionSignals.join(', ')}>
                  {' '}
                  · {formatDetection.detectionSignals.length} detection signal(s)
                </span>
              )}
            </p>
          )}

          <label className="piw-checkbox-row">
            <input
              type="checkbox"
              checked={linkToExistingExercises}
              onChange={(e) => setLinkToExistingExercises(e.target.checked)}
            />
            <span>
              <strong>Link to existing exercises</strong> when the name matches the library (ignores casing).
              Linked exercises keep their existing description/instructions (imported prose still appears in workout-line notes).
              Uncheck to always create new exercises from this import.
            </span>
          </label>

          <div className="piw-stats">
            <span><strong>{sessionCount}</strong> workouts / sessions</span>
            <span><strong>{exerciseLineCount}</strong> exercise lines</span>
            <span><strong>{historySessionCount}</strong> imported history entries</span>
          </div>

          {warnings.length > 0 && (
            <div className="piw-warnings">
              <strong>Warnings</strong>
              <ul>
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="piw-session-list">
            {bundle.sessions.map((s, idx) => (
              <details key={idx} className="piw-session" open={idx === 0}>
                <summary>
                  {s.ordinal}. {s.workoutName}
                  {s.label ? ` — ${s.label}` : ''}
                </summary>
                <ul className="piw-ex-list">
                  {s.blocks.flatMap((b) =>
                    (b.exercises || []).map((ex, j) => (
                      <li key={`${b.name}-${j}`}>
                        <span className="piw-ex-name">{ex.name}</span>
                        <span className="piw-ex-stats">
                          {ex.sets != null && `${ex.sets} sets`}
                          {ex.reps != null && ` × ${ex.reps} reps`}
                          {ex.durationSec != null && ` · ${ex.durationSec}s`}
                          {ex.restSec != null && ` · rest ${ex.restSec}s`}
                        </span>
                      </li>
                    ))
                  )}
                </ul>
              </details>
            ))}
          </div>

          <div className="piw-actions">
            <button type="button" className="piw-secondary" onClick={() => setStep(1)} disabled={loading}>
              Back
            </button>
            <button type="button" className="piw-primary" onClick={handleCommit} disabled={loading}>
              {loading ? 'Importing…' : (
                <>
                  <FaCheck /> Import to FreshFitLab
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
